"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Check, ChevronDown, ListChecks, X } from "lucide-react";
import * as React from "react";
import ReactMarkdown from "react-markdown";

import { cn } from "../../lib/cn";
import { LoginBlob } from "../blob";
import { type ThinkingStepGroup, groupThinkingHops, stepDurationMs } from "./thinkingChainGroups";

/**
 * ThinkingChain — visible request activity, derived from backend events.
 *
 * Drop-in replacement for `<StreamingIndicator>` when the BE has emitted
 * at least one `reasoning_hop` event for the active query. The plain-dots
 * indicator still owns the 0–1.5s warm-up window; once the agent surfaces
 * its first hop we hand off to this richer block.
 *
 * Visual model (Oct 2026 — a short work diary inside the assistant turn):
 *
 *   collapsed (default while thinking) — zero chrome, one hairline rail
 *   ◉  ANDO · sta pensando · 71.2s                           ✕
 *   │
 *   │  ●  Provo il calcolo sui dati aziendali…  ×3
 *   │                                          ▾ 6 passaggi
 *
 *   expanded
 *   ◉  ANDO · sta pensando · 71.2s                           ✕
 *   │
 *   │  ✓  Analizzo la richiesta                          2.6s
 *   │  ✓  Apro l'analisi del database selezionato…       1.4s
 *   │  ✓  Provo il calcolo sui dati aziendali…  ×4       3.1s
 *   │  ✓  Valido il risultato finale…                    1.2s
 *   │  ●  Provo il calcolo sui dati aziendali…  ×3
 *   │                                          ▴ 6 passaggi
 *
 * The raw hop stream is one event per agent graph node (LLM call →
 * filler "Sto ragionando…", tool call → tool copy), so a single SQL turn
 * carries 20+ hops. `groupThinkingHops` folds fillers into the previous
 * step and merges consecutive repeats (×N); the card shows only the
 * latest three steps until the user asks for the full timeline. The specialist
 * label column is rendered only when the turn actually spans more than
 * one specialist (multi-source RAG) — for the synthetic `agent` hops it
 * was pure repetition.
 *
 * Design tokens: NO container (no card, blur or shadow — the block is
 * typography on the paper, like the `<StreamingIndicator>` it replaces,
 * so the handoff reads as one element growing a body), a single hairline
 * rail hanging from the blob's centre, the mini 3D blob as the only brand
 * anchor (DRY: `<LoginBlob size="mini">`), one orange accent for the
 * active step, `tabular-nums` timings, Lucide `Check` for done steps.
 * Header morphs to "sta scrivendo" + a thin progress bar when the first
 * answer token arrives. Completed activity remains expandable below its answer.
 *
 * Accessibility:
 *   - `<output>` + `aria-live="polite"` + `aria-busy` while active
 *   - Each step marker exposes `aria-label="completato" / "in corso" / …`
 *   - The timeline toggle is a real button (`aria-expanded` / `aria-controls`)
 *   - Every entrance animation lives behind `motion-safe:`
 *
 * Performance:
 *   - The blob mini chunk is already lazy-loaded inside `<LoginBlob>`.
 *     Status dots are pure CSS keyframes (no JS raf).
 *   - Grouping is memoised per `hops` reference (Zustand hands a new
 *     array only when a hop lands).
 */

export type ThinkingChainHopStatus = "idle" | "active" | "done" | "error";

export interface ThinkingChainHop {
  /** Stable id — used for React keys + animation triggers. */
  id: string;
  /** BE specialist (`email` | `documents` | `chat` | …). Drives icon + label. */
  specialist: string;
  /** Wire action, retained to distinguish a real specialist start from a tool step. */
  action?: string;
  /** Localised IT label, e.g. "Email", "Documenti". */
  label: string;
  /** Short action description ("Cerco fattura agosto"). */
  detail?: string;
  status: ThinkingChainHopStatus;
  /**
   * `filler` marks the per-LLM-call "Sto ragionando…" tick the BE emits
   * between tool calls (`action === "model_request"`). Fillers never get
   * their own row — they extend the previous step. Defaults to `step`.
   */
  kind?: "step" | "filler";
  /** Epoch-ms timestamp at first sighting. */
  startedAt?: number;
  /** Epoch-ms timestamp when the next hop arrived (= "done"). */
  endedAt?: number;
  /** Expanded-view full content (BE `user_details` / `details`). */
  contentFull?: string;
  /** Aggregated doc count for the hop (rendered in expanded view). */
  sourcesFound?: number;
}

export interface ThinkingChainProps {
  hops: readonly ThinkingChainHop[];
  /** A finished answer keeps its activity available below the message. */
  completed?: boolean;
  /** First token of the answer text has landed → header switches state. */
  isAnswering?: boolean;
  /** Explicit operational status, for example cancellation awaiting acknowledgment. */
  statusLabel?: string;
  /** Milliseconds since `start` event — drives the live timer in the header. */
  elapsedMs?: number;
  /** Click handler for the Interrompi button. When omitted the button is hidden. */
  onStop?: () => void;
  /** Extra classes on the outer wrapper (alignment / spacing). */
  className?: string;

  /**
   * Optional, BE-sanitised provider summary accumulated from ``thinking_delta``
   * WS events. When present and non-empty AND ``isAnswering=false``,
   * a quiet text block renders below the hop list. When the answer starts (isAnswering=true) or the
   * reasoning has finalised, the block collapses into a compact
   * "Sintesi del modello" chip that expands on click to show the full
   * summary that was actually received.
   *
   * Undefined = model didn't emit any thinking (e.g. ``thinking_level=MINIMAL``);
   * the surface renders exactly like before this SOTA extension.
   */
  streamingThinking?: string;
  /**
   * True once ``finalizeReasoning`` fires — the live block collapses to the
   * disclosure. Independent of ``isAnswering`` because the
   * two events are technically distinct on the wire (BE could emit
   * ``answer_delta`` and only later emit ``complete``).
   */
  thinkingFinalized?: boolean;
  /**
   * Duration of the thinking window in ms
   * (``thinkingEndedAt - thinkingStartedAt``). Shown in the chip label
   * as ``Ragionamento (X.Ys)``. Null / undefined hides the elapsed suffix.
   */
  thinkingDurationMs?: number | null;
}

function formatElapsed(ms: number | undefined): string {
  if (typeof ms !== "number" || ms < 0) return "0.0s";
  return `${(ms / 1000).toFixed(1)}s`;
}

/** Map a hop to its accessible dot label. */
function dotAriaLabel(status: ThinkingChainHopStatus): string {
  if (status === "done") return "completato";
  if (status === "active") return "in corso";
  if (status === "error") return "errore";
  return "in arrivo";
}

export const ThinkingChain: React.FC<ThinkingChainProps> = ({
  hops,
  completed = false,
  isAnswering = false,
  statusLabel,
  elapsedMs,
  onStop,
  className,
  streamingThinking,
  thinkingFinalized = false,
  thinkingDurationMs,
}) => {
  const { i18n } = useLingui();
  const [expanded, setExpanded] = React.useState(false);
  const [stepsExpanded, setStepsExpanded] = React.useState(false);
  const [thinkingChipExpanded, setThinkingChipExpanded] = React.useState(false);
  const stepsId = React.useId();
  const completedActivityId = React.useId();

  // Sept 2026 — fold the raw event stream into user-facing steps once per
  // hop batch. Specialist labels only earn a column when the turn spans
  // more than one specialist; otherwise they would repeat on every row.
  const steps = React.useMemo(() => groupThinkingHops(hops), [hops]);

  // Completed turns use a single quiet disclosure; the live header only
  // describes the current request phase.
  const headerStatus = isAnswering ? "sta scrivendo" : "sta lavorando";
  const elapsedLabel = formatElapsed(elapsedMs);

  // The optional provider summary is live-streamed WHILE the model
  // is still pre-answering (isAnswering=false, not finalized) and
  // collapses to a chip the moment the answer arrives or reasoning ends.
  const hasThinking = Boolean(streamingThinking?.trim());
  const showLiveThinking = hasThinking && !isAnswering && !thinkingFinalized;
  const showThinkingChip = hasThinking && (isAnswering || thinkingFinalized);

  // Once the answer exists, its content owns the visual hierarchy. Keep
  // the same grouped timeline available without repeating a full work diary
  // below every response (including restored conversation history).
  if (completed) {
    if (steps.length === 0) return null;
    return (
      <div className={cn("w-full max-w-[640px]", className)}>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          aria-label={i18n._(msg`Attività della richiesta`)}
          title={i18n._(msg`Attività della richiesta`)}
          aria-controls={completedActivityId}
          className={cn(
            "inline-flex min-h-6 items-center gap-1.5 rounded-md px-1 text-[11px] text-ink-3",
            "transition-colors hover:text-ink-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
          )}
        >
          <ListChecks className="h-3 w-3" aria-hidden />
          {expanded && <span>{steps.length} passaggi</span>}
          <ChevronDown
            className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")}
            aria-hidden
          />
        </button>
        {expanded ? (
          <div id={completedActivityId} className="ml-1 border-hairline border-l pl-4">
            <ThinkingSteps
              steps={steps}
              expanded
              ariaLabel={i18n._(msg`Attività della richiesta`)}
              id={stepsId}
              showToggle={false}
            />
            {hops.some((hop) => hop.contentFull) ? (
              <ThinkingChainDetails
                hops={hops}
                expanded={stepsExpanded}
                onToggle={() => setStepsExpanded((value) => !value)}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <output
      aria-live="polite"
      aria-busy="true"
      className={cn("block w-full max-w-[640px]", "motion-safe:animate-msg-in", className)}
    >
      {/* Zero chrome (Sept 2026): no card, no blur, no shadow. The block
          is typography on the paper, exactly like the `<StreamingIndicator>`
          that precedes it — the handoff reads as the same element growing
          a body, not a box popping in. Structure comes from ONE hairline
          rail under the blob's centre (blob 32px → rail at 16px). */}
      <div className="relative">
        <ThinkingChainHeader
          status={statusLabel || headerStatus}
          elapsedLabel={elapsedLabel}
          onStop={onStop}
          completed={completed}
        />

        <div className="ml-[15px] flex flex-col border-hairline border-l pl-[22px]">
          {/* Steps stay available through writing and after completion. */}
          {steps.length > 0 ? (
            <ThinkingSteps
              steps={steps}
              expanded={stepsExpanded}
              onToggle={() => setStepsExpanded((v) => !v)}
              ariaLabel={i18n._(msg`Attività della richiesta`)}
              id={stepsId}
            />
          ) : null}

          {/* Optional provider summary. Rendered
            below the hop list while the model is still pre-answering,
            replaced by the compact chip once the answer starts
            streaming or reasoning finalises. */}
          {showLiveThinking && streamingThinking ? (
            <LiveThinkingBlock text={streamingThinking} />
          ) : null}

          {/* Answering progress bar accompanies the hop list while tokens arrive. */}
          {isAnswering && !completed ? <AnsweringProgressBar /> : null}

          {/* Collapsible provider-summary chip. Shown
            when the answer is streaming or the reasoning has ended;
            expands on click to reveal the full sanitised summary. */}
          {showThinkingChip && streamingThinking ? (
            <ThinkingChip
              text={streamingThinking}
              durationMs={thinkingDurationMs ?? null}
              expanded={thinkingChipExpanded}
              onToggle={() => setThinkingChipExpanded((v) => !v)}
            />
          ) : null}

          {/* Expandable details — only meaningful when at least one hop
            carries a `contentFull` payload. */}
          {hops.some((h) => h.contentFull) ? (
            <ThinkingChainDetails
              hops={hops}
              expanded={expanded}
              onToggle={() => setExpanded((v) => !v)}
            />
          ) : null}
        </div>
      </div>
    </output>
  );
};
ThinkingChain.displayName = "ThinkingChain";

/** Header row — anchor dot + shimmer label + live timer + Interrompi. */
const ThinkingChainHeader: React.FC<{
  status: string;
  elapsedLabel: string | null;
  onStop?: () => void;
  completed?: boolean;
}> = ({ status, elapsedLabel, onStop, completed }) => (
  <div className="relative flex items-center gap-2.5 py-1 pr-1">
    {/* Brand anchor — live WebGL Ando blob, same 32px as the
        `<StreamingIndicator>` so the handoff between the two is
        pixel-stable. The rail below hangs from this blob's centre. */}
    <span aria-hidden className="block h-8 w-8 shrink-0">
      {completed ? (
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ando-orange/10 text-ando-orange">
          <Check size={16} strokeWidth={2} />
        </span>
      ) : (
        <LoginBlob size="mini" />
      )}
    </span>

    <div className="flex min-w-0 flex-1 items-baseline gap-1.5">
      <span className="font-medium text-[10px] text-ando-orange uppercase tracking-[0.08em]">
        Ando
      </span>
      <span className="text-[10px] text-ink-3/60">·</span>
      <span
        className={cn(
          "truncate font-medium text-[13px] leading-tight tracking-[-0.005em]",
          !completed && "shimmer-text",
        )}
      >
        {status}
      </span>
      {elapsedLabel ? (
        <>
          <span className="text-[10px] text-ink-3/60">·</span>
          <span className="font-medium text-[11px] text-ink-2 tabular-nums">{elapsedLabel}</span>
        </>
      ) : null}
    </div>

    {onStop ? (
      <button
        type="button"
        onClick={onStop}
        aria-label="Interrompi"
        className={cn(
          // 28px visual button, 44px hit area via -inset padding
          "-m-2 relative inline-flex h-11 w-11 shrink-0 items-center justify-center",
          "rounded-full text-ink-3 transition-colors",
          "hover:bg-black/[0.04] hover:text-ink-1",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
        )}
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    ) : null}
  </div>
);

/**
 * Steps block — collapsed: only the current step; expanded: the whole
 * deduplicated timeline. The toggle ("N passaggi") only appears when
 * there is something to reveal (≥ 2 steps).
 */
const ThinkingSteps: React.FC<{
  steps: readonly ThinkingStepGroup[];
  expanded: boolean;
  onToggle?: () => void;
  ariaLabel: string;
  id: string;
  showToggle?: boolean;
}> = ({ steps, expanded, onToggle, ariaLabel, id, showToggle = true }) => {
  const current = steps.at(-1);
  if (!current) return null;
  // Keep a short, chronological work diary visible in the assistant turn.
  // The previous one-line view hid useful progress until the user expanded it.
  const visible = expanded ? steps : steps.slice(-3);
  const visibleStart = steps.length - visible.length;
  const canExpand = showToggle && steps.length > 3;

  return (
    <div className="pt-2 pb-1">
      <ol id={id} className="relative flex flex-col gap-1.5" aria-label={ariaLabel}>
        {visible.map((step, idx) => {
          const prior = steps[visibleStart + idx - 1];
          const namedSpecialist = !["agent", "all", "analysis", "chat"].includes(step.specialist);
          const handoff =
            step.action?.endsWith("_start") ||
            (prior !== undefined && prior.specialist !== step.specialist) ||
            (idx === 0 && namedSpecialist);
          return <ThinkingStepRow key={step.id} step={step} index={idx} handoff={handoff} />;
        })}
      </ol>
      {canExpand && onToggle ? (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={id}
            className={cn(
              // 44px hit area via generous padding; visually a ghost text link.
              "-mr-2 -mb-1 inline-flex min-h-[32px] items-center gap-1 rounded-md px-2",
              "font-medium text-[11px] text-ink-3 tabular-nums transition-colors",
              "hover:text-ink-1",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
            )}
          >
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-200",
                expanded && "rotate-180",
              )}
              aria-hidden
            />
            {expanded ? "Mostra meno" : `Mostra tutti i ${steps.length} passaggi`}
          </button>
        </div>
      ) : null}
    </div>
  );
};

/** One user-facing step: marker, optional specialist, copy, ×N, duration.
 *  Typography-first — the header blob is the brand anchor, the active
 *  step is the only orange element in the list. */
const ThinkingStepRow: React.FC<{
  step: ThinkingStepGroup;
  index: number;
  handoff: boolean;
}> = ({ step, index, handoff }) => {
  const durationMs = step.status === "active" ? null : stepDurationMs(step);
  const isActive = step.status === "active";

  return (
    <li
      className="flex flex-col gap-0.5"
      style={{ animationDelay: `${Math.min(index, 6) * 40}ms` }}
    >
      {handoff ? (
        <span className="pl-6 font-medium text-[10px] text-ink-3 uppercase tracking-[0.08em]">
          {step.label}
        </span>
      ) : null}
      <div
        className={cn(
          "flex min-h-[20px] items-center gap-2.5",
          "motion-safe:fade-in motion-safe:slide-in-from-left-2 motion-safe:animate-in motion-safe:duration-300",
        )}
      >
        <StepMarker status={step.status} />
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-[12.5px] leading-tight",
            isActive && "shimmer-text font-medium",
            step.status === "done" && "text-ink-2",
            step.status === "idle" && "text-ink-3",
            step.status === "error" && "text-rose-600",
          )}
        >
          {step.detail || (step.status === "idle" ? "in arrivo" : "in corso…")}
        </span>
        {step.count > 1 ? (
          <span className="shrink-0 text-[11px] text-ink-3 tabular-nums">×{step.count}</span>
        ) : null}
        {durationMs !== null ? (
          <span className="shrink-0 text-[11px] text-ink-3 tabular-nums">
            {formatElapsed(durationMs)}
          </span>
        ) : null}
      </div>
    </li>
  );
};

/** Step marker — Lucide check for done, pulsing orange dot for active,
 *  hollow dot for idle, rose dot for error. Fixed 14px box so the copy
 *  column stays aligned across marker kinds. */
const StepMarker: React.FC<{ status: ThinkingChainHopStatus }> = ({ status }) => (
  <span
    role="img"
    aria-label={dotAriaLabel(status)}
    className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center"
  >
    {status === "done" ? (
      <Check className="h-3.5 w-3.5 text-ink-3" strokeWidth={2.25} aria-hidden />
    ) : (
      <span
        className={cn(
          "inline-block h-2 w-2 rounded-full",
          status === "idle" && "border border-black/15 bg-transparent",
          status === "active" &&
            "bg-ando-orange shadow-[0_0_0_3px_rgba(255,160,87,0.18)] motion-safe:animate-pulse",
          status === "error" && "bg-rose-500",
        )}
      />
    )}
  </span>
);

/** Thin orange progress bar shown while the LLM is streaming the answer. */
const AnsweringProgressBar: React.FC = () => (
  <div
    aria-hidden
    className="relative my-2.5 h-[2px] w-full overflow-hidden rounded-full bg-black/[0.04]"
  >
    <span
      className={cn(
        "absolute inset-y-0 left-0 w-1/3 rounded-full bg-ando-orange/80",
        "motion-safe:animate-[thinking-slide_1.4s_ease-in-out_infinite]",
      )}
      style={
        {
          // Inline keyframes via `@keyframes thinking-slide` are defined in
          // packages/ui/src/styles.css (chat block). We add it there so the
          // class above resolves without a new Tailwind preset entry.
        }
      }
    />
  </div>
);

/**
 * Optional provider summary, not the model's private chain of thought.
 * The operational timeline above remains useful when Gemini sends no summary.
 *
 * Accessibility:
 *   - Wrapped in ``<section role="log" aria-live="polite">`` so screen
 *     readers can pick up incremental thinking without interrupting.
 *   - Kept keyboard-inert — the block is passive; interaction moves to
 *     the ``ThinkingChip`` after finalise.
 *
 * DRY: uses the same border-hairline / typography-first tokens as the
 * hop list above; no new CSS variables introduced.
 */
const LiveThinkingBlock: React.FC<{ text: string }> = ({ text }) => {
  const visible = text
    .trim()
    .split(/\n\s*\n/)
    .slice(-2)
    .join("\n\n");

  return (
    <section
      role="log"
      aria-live="polite"
      aria-label="Sintesi del modello in corso"
      className="py-2"
    >
      <div className="min-w-0">
        <span className="font-medium text-[11px] text-ink-3">Sintesi del modello</span>
        <div className="mt-1 max-h-[5.5rem] overflow-hidden break-words text-[13px] text-ink-2 leading-relaxed [&_p]:mb-1 [&_strong]:font-semibold">
          <ReactMarkdown
            allowedElements={["p", "strong", "em", "ul", "ol", "li", "code"]}
            unwrapDisallowed
          >
            {visible}
          </ReactMarkdown>
        </div>
      </div>
    </section>
  );
};

/**
 * Compact "Sintesi del modello (X.Ys)" chip that supersedes the live block
 * once the answer starts streaming. Click to expand into the received
 * and sanitised provider summary, never private chain of thought.
 *
 * Accessibility: ``aria-expanded`` + focus ring; hit-slop 44 px via
 * padding; the expanded region has a stable ``id`` so a future context
 * menu can reference it.
 */
const ThinkingChip: React.FC<{
  text: string;
  durationMs: number | null;
  expanded: boolean;
  onToggle: () => void;
}> = ({ text, durationMs, expanded, onToggle }) => {
  const elapsed = durationMs !== null && durationMs > 0 ? formatElapsed(durationMs) : null;
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls="thinking-chain-thinking-body"
        className={cn(
          "-ml-2 flex w-[calc(100%+0.5rem)] items-center gap-2 rounded-md px-2 py-2",
          "font-medium text-[11px] text-ink-2",
          "transition-colors hover:bg-black/[0.02] hover:text-ink-1",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
        )}
      >
        <span className="flex-1 text-left">
          Sintesi del modello
          {elapsed ? <span className="ml-1 text-ink-3/80 tabular-nums">· {elapsed}</span> : null}
        </span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-ink-3 transition-transform duration-200",
            expanded && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      <div
        id="thinking-chain-thinking-body"
        className={cn(
          "overflow-hidden transition-[max-height,opacity] duration-300 ease-out",
          expanded ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <div className="pb-2">
          <div
            className={cn(
              "break-words px-1 py-1 [&_p]:mb-2 [&_strong]:font-semibold",
              "text-[13px] text-ink-2 leading-relaxed",
              "max-h-[360px] overflow-y-auto",
            )}
          >
            <ReactMarkdown
              allowedElements={["p", "strong", "em", "ul", "ol", "li", "code"]}
              unwrapDisallowed
            >
              {text}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Collapsible "Mostra dettagli" footer + expanded content. */
const ThinkingChainDetails: React.FC<{
  hops: readonly ThinkingChainHop[];
  expanded: boolean;
  onToggle: () => void;
}> = ({ hops, expanded, onToggle }) => (
  <div>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls="thinking-chain-details"
      className={cn(
        "-ml-2 flex items-center gap-1.5 rounded-md",
        "px-2 py-1.5 font-medium text-[11px] text-ink-3",
        "transition-colors hover:bg-black/[0.02] hover:text-ink-1",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
      )}
    >
      <ChevronDown
        className={cn("h-3.5 w-3.5 transition-transform duration-200", expanded && "rotate-180")}
        aria-hidden
      />
      {expanded ? "Nascondi dettagli" : "Mostra dettagli"}
    </button>
    <div
      id="thinking-chain-details"
      className={cn(
        "overflow-hidden transition-[max-height,opacity] duration-300 ease-out",
        expanded ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0",
      )}
    >
      <div className="flex flex-col gap-2 pb-2">
        {hops
          .filter((h) => h.contentFull)
          .map((hop, idx) => (
            <div
              key={`${hop.id}-${idx}-detail`}
              className="rounded-lg border border-hairline bg-white/40 px-3 py-2"
            >
              <p className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
                {hop.label}
                {typeof hop.sourcesFound === "number" && hop.sourcesFound > 0 ? (
                  <span className="ml-1.5 text-ando-orange">· {hop.sourcesFound} fonti</span>
                ) : null}
              </p>
              <p className="mt-1 text-[12px] text-ink-1 leading-relaxed">{hop.contentFull}</p>
            </div>
          ))}
      </div>
    </div>
  </div>
);
